import $ from 'jquery'
import {ChartSortOptions, ChartType, ReportGroupingLevel} from '../constants'
import NumberFormatter from '../../number_formatter'
import messageResource from '../../messageResource'
import ReportChartUtils from '../ReportChartUtils'
import ChartFormatterCreator from './parametersFormatters/ChartFormatterCreator'
import {OTHER_USER_GROUPS_ENTITY_ID, OTHER_USERS_ENTITY_ID} from "../../constants"
import {escapeHtmlSymbols} from '../../atUtils'

const AmCharts = window.AmCharts

export default function ChartController() {
}

ChartController.prototype.init = function( config )
{
    this.containerId = config.containerId;
    this.partsContainer = $("#" + config.partsContainerId);
    this.chartFormatter = new ChartFormatterCreator();
    this.currentData = config.data;
    this.currentState = config.settings;
    this._extendCurrentStateWithFormattedFunctions( config.settings.groupingLevel, config.settings.reportChartType );

    if( this.currentState.sortOption === ChartSortOptions.BY_VALUES)
        this.sortDataByValues( this.currentState.sortOrder, this.currentState.sortCategoryComparator);
    else
        this.sortDataByCategory( this.currentState.sortOrder,  this.currentState.sortCategoryComparator);

    this.numberFormatter = new NumberFormatter(window.SystemSettings.decimalSeparator, window.SystemSettings.digitGroupingSymbol, 2, false);

    if (!this.currentData.chartPoints || !this.currentData.chartPoints.length)
        return this;

    this.chart =  this.showChart( this.currentData, this.currentState);

    return this;
};

ChartController.prototype.getBarChartConfiguration = function( chartPoints, title, settings ) {
    let dataProvider = this._getDataProvider(chartPoints, this.currentState.groupingLevel, this.currentState.stackColumns)

    const conf = {
        "type": "serial",
        "rotate" : settings.horizontal,
        "categoryField": "category",
        "startDuration": settings.withoutAnimation ? 0 : 0.3,
        "startEffect": "easeOutSine",
        "sequencedAnimation": false,
        "theme": AmCharts.themes["light"],
        "fontSize": settings.fontSize ? settings.fontSize : 11,
        "chartCursor": {
            "categoryBalloonFunction": settings.horizontal
                                       ? this.currentState.formatHBarChartCursorLabelFunction
                                       : this.currentState.formatVBarChartCursorLabelFunction,
            "categoryBalloonEnabled": !this.currentState.stackColumns,
        },
        "categoryAxis": {
            "color": "#414141",
            "labelRotation": 48,
            "labelFrequency": 1,
            "minHorizontalGap": 65,
            "minVerticalGap" : 50,
            "labelOffset": settings.horizontal ? 11 : 0,
            "categoryFunction" : this.currentState.formatCategoryFunction
        },
        "chartScrollbar": settings.withoutScroll ?
                          false :
                          {
                              "scrollbarHeight": 16,
                              "dragIconHeight": 30,
                              "dragIconWidth": 30,
                              "backgroundColor" : "#f9f9f9",
                              "backgroundAlpha" : 1,
                              "selectedBackgroundAlpha" : 1,
                              "selectedBackgroundColor" : "#e8e8e8"
                          } ,
        "graphs": this._buildGraphs(settings),
        "valueAxes": [
            {
                "stackType": this.currentState.stackColumns ? "regular" : "none",
                "gridCount": 5,
                "labelFrequency": 1,
                "autoGridCount": false,
                "includeAllValues": true,
                "minHorizontalGap": 70,
                "labelFunction": this.currentState.formatValueFunction,
                "fontSize": 11,
                "title": title
            }
        ],
        "titles": [
            {
                "text": ""
            }
        ],
        "dataProvider": dataProvider,
        "export": {
            "enabled": true,
            "menu": [],
            "libs" : {
                "autoLoad": false
            }
        }
    }

    if( settings.minimum )
        conf.valueAxes[0].minimum = settings.minimum;
    else if (this.currentData.min > 0)
        conf.valueAxes[0].minimum = 0;
    else
        conf.valueAxes[0].minimum = this.currentData.min * 1.1;

    if( settings.maximum )
        conf.valueAxes[0].maximum = settings.maximum;
    else if (this.currentData.max < 0)
        conf.valueAxes[0].maximum = 0;
    else
        conf.valueAxes[0].maximum = this.currentData.max * 1.1;

    return conf;
};

ChartController.prototype.showChart = function(data, currentState) {
    if(this.chart) this.chart.clear() // clear previous chart for performance issues

    switch (currentState.chartType) {
        case ChartType.HORIZONTAL_BAR:
            return this.buildBarChart(this.containerId,
                this.getBarChartConfiguration(
                    data.chartPoints,
                    data.title,
                    {horizontal: true},
                )
            )
        case ChartType.VERTICAL_BAR:
            return this.buildBarChart(this.containerId,
                this.getBarChartConfiguration(
                    data.chartPoints,
                    data.title,
                    {},
                )
            )
        case ChartType.PIE_CHART:
        {
            if (data.max === 0 && data.min === 0) this.currentState.emptyPieCallback()
            return this.buildPieChart(
                this.containerId,
                this.buildPieChartDataProvider(data.min, data.max),
                11,
                290,
                true
            )
        }
    }
}

ChartController.prototype.buildPieChartDataProvider = function( minValue, maxValue ) {
    const plainDataProvider = [];

    const abs = !isNaN(maxValue) && !isNaN(minValue) //for chart we need positive values but for data-tables we need values "as-is"
    const filter = abs && (minValue < 0 && maxValue > 0)
    const colorScheme = ReportChartUtils.getChartColorSchemeById(this.currentState.colorId)
    $.each(this.currentData.chartPoints, (i, item) => {
        const value = item.value.total
        if (!filter || value > 0)
            plainDataProvider.push({
                sectorColor: this._isOtherUsersData(this.currentState.groupingLevel, item) ? colorScheme.notAssigned : undefined,
                category: item.category,
                value: abs ? Math.abs(value) : value,
                formattedValue: this.currentState.formatValueFunction(value)
            })
    })
    return plainDataProvider;
};

ChartController.prototype.buildPieChart = function( container, dataProvider, fontSize, maxLabelWidth, animate, onRenderedHandler ) {
    const chart = new AmCharts.AmPieChart();
    if( onRenderedHandler )
        chart.addListener( "rendered", function ( e )
        {
            onRenderedHandler( e );
        } );
    chart.addListener( "drawn", function ( e )
    {
        if (document.createElementNS && e.chart.realHeight > 0 && e.chart.realWidth > 0 ) {
            const rectangle= document.createElementNS('http://www.w3.org/2000/svg', "rect");
            rectangle.setAttribute("width", e.chart.realWidth);
            rectangle.setAttribute("height", e.chart.realHeight * 0.08);
            rectangle.setAttribute("fill", "white");
            $(e.chart.chartDiv).find(".amcharts-title").before(rectangle);
        }
    } );

    const numberFormatter = this.numberFormatter
    const toStringFunction = this.currentState.toStringFunction
    const formatLabelFunction = this.currentState.formatLabelFunction
    const self = this
    AmCharts.extend( chart, {
        "type": "pie",
        "startDuration": animate ? 0.6 : 0,
        "startEffect": "easeOutSine",
        "startRadius": 120,
        "sequencedAnimation": false,
        "balloonFunction": function ( item )
        {
            return "<div class='pieChartBalloon'>" +
                   escapeHtmlSymbols(self.currentState.toStringFunction(item.dataContext.category)) +
                   "<br><span style='font-size:14px'><b>" +
                   item.dataContext.formattedValue +
                   " </b>(" +
                   numberFormatter.format(item.percents) +
                   "%)</span>" +
                "</div>";
        },
        "dataProvider": dataProvider,
        "titleField": "category",
        "valueField": "value",
        "outlineThickness": 1,
        "outlineAlpha": 1,
        "fontSize" : fontSize,
        "labelRadius": 20,
        "maxLabelWidth": maxLabelWidth,
        "hideLabelsPercent": 1.5,
        "labelFunction": (data) => {
            const title = toStringFunction(data.title)
            return formatLabelFunction(title)
        },
        "radius": "30%",
        "innerRadius": "38%",
        "titles": [
            {
                "text": this.currentData.title
            }
        ],
        "export": {
            "enabled": true,
            "menu" : [],
            "libs" : {
                "autoLoad": false
            }
        },
        "colorField": "sectorColor",
        "colors": ["#5e9fd9", "#3fbe67", "#9fdf4d", "#ffd600", "#f6ad19", "#f77a36", "#df3c72", "#a44fce", "#5470c0", "#189195", "#649f2f",
                   "#426ec7", "#2c8a49", "#98911e", "#d28e03", "#d65c1c", "#c74419", "#b72c59", "#723d98", "#475699", "#0a7399"]
    } );

    AmCharts.isReady ?
    chart.write( container )
            : AmCharts.ready( function ()
                              {
                                  chart.write( container );
                              } );

    return chart;
};

ChartController.prototype.buildBarChart = function( container, settings, onRenderedHandler ) {

    const theme = AmCharts.themes["light"];
    const chart = new AmCharts.AmSerialChart( theme );
    const self = this;
    if( onRenderedHandler ){
        chart.addListener( "rendered", function ( e )
        {
            onRenderedHandler( e );
        } );
    } else
    {
        chart.addListener( "zoomed", function ( e )
        {
            self.currentBarCount = e.endIndex - e.startIndex + 1;
        } );
    }

    AmCharts.extend( chart, settings );

    AmCharts.isReady ?
    chart.write( container )
            : AmCharts.ready( function ()
                              {
                                  chart.write( container );
                              } );
    return chart;
};

ChartController.prototype.sortByValues = function ( asc, secondaryComparator ) {
    this.currentState.sortOrder = asc;
    this.sortDataByValues(asc, secondaryComparator);
    this.chart = this.showChart( this.currentData, this.currentState );
};

ChartController.prototype.sortDataByValues = function ( asc, secondaryComparator ) {
    this.currentData.chartPoints.sort(
        (x, y) => {
            let xValue = x.value.total
            let yValue = y.value.total

            if (xValue === yValue) return secondaryComparator(x.category, y.category)

            return xValue < yValue ? (asc ? -1 : 1) : (asc ? 1 : -1)
        }
    )
}

ChartController.prototype.sortByCategory = function ( asc, comparator) {
    this.currentState.sortCategoryComparator = comparator;
    this.currentState.sortOrder = asc;
    this.sortDataByCategory(asc, comparator);
    this.chart = this.showChart( this.currentData, this.currentState );
};

ChartController.prototype.sortDataByCategory = function(asc, comparator){
    this.currentData.chartPoints.sort( function(x, y) { return asc ? comparator( x.category, y.category) : -comparator( x.category, y.category) } );
};

ChartController.prototype.changeChartType = function ( type ) {
    this.currentState.chartType = type;
    this.currentState.stackColumns = type !== ChartType.PIE_CHART
    return this.showChart( this.currentData, this.currentState );
};

ChartController.prototype.changeShowPercents = function ( isShown ) {
    this.currentState.showPercents = isShown;
    this.chart = this.showChart( this.currentData, this.currentState );
};

ChartController.prototype.changeStackColumns = function ( stackColumns ) {
    this.currentState.stackColumns = stackColumns;
    this.chart = this.showChart( this.currentData, this.currentState );
};

ChartController.prototype.getCurrentDataProvider = function () {
    return this.chart.dataProvider;
};

ChartController.prototype.updateDisplayData = function(config){
    this.currentState.colorId = config.colorId
    this.currentState.chartType = config.chartType;
    this.currentState.sortOption = config.sortOption;
    this.currentState.sortOrder = config.sortOrder;
    this.currentState.showPercents = config.isShowPercents;
    this.currentState.stackColumns = config.stackColumns;
    if( this.currentState.sortOption === ChartSortOptions.BY_VALUES)
        this.sortDataByValues( this.currentState.sortOrder, this.currentState.sortCategoryComparator );
    else
        this.sortDataByCategory( this.currentState.sortOrder,  this.currentState.sortCategoryComparator);

    this.chart = this.showChart( this.currentData, this.currentState );
};

ChartController.prototype.updateData = function ( data, state ) {
    this.currentData = data;
    this.currentState.sortCategoryComparator =state.sortCategoryComparator;
    this.currentState.sortOption = state.sortOption;
    this.currentState.sortOrder = state.sortOrder;
    this.currentState.showPercents = state.isShowPercents;
    this.currentState.stackColumns = state.stackColumns;
    this.currentState.groupingLevel = state.groupingLevel;
    this._extendCurrentStateWithFormattedFunctions( state.groupingLevel, state.reportChartType );

    if( this.currentState.sortOption === ChartSortOptions.BY_VALUES)
        this.sortDataByValues( this.currentState.sortOrder, this.currentState.sortCategoryComparator);
    else
        this.sortDataByCategory( this.currentState.sortOrder,  this.currentState.sortCategoryComparator);

    this.chart = this.showChart(this.currentData, this.currentState)
};

ChartController.prototype.exportChart = function( size, callback, barCount ) {
    this.partsContainer.width(size.width);
    this.partsContainer.height(size.height);

    const breaker = { 'break': false };
    this.currentExportBreaker = breaker;

    switch (this.currentState.chartType) {
        case ChartType.HORIZONTAL_BAR:
            this._exportBarChart( barCount, true, breaker, callback );
            return;
        case ChartType.VERTICAL_BAR:
            this._exportBarChart( barCount, false, breaker, callback );
            return;
        case ChartType.PIE_CHART:
            this._exportPieChart( callback );
            return;
    }
};

ChartController.prototype._exportPieChart = function( callback ) {
        const result = [{
        data: this.buildPieChartDataProvider()
    }];

    const getIndexOf = function(array, item) {
        const r = $.map(array, function(obj, index) {
            if(Math.abs(obj.value) === Math.abs(item.value) && obj.category === item.category) {
                return index;
            }
        });
        return r.length ? r[0] : -1;
    };

    this.buildPieChart( "chartPart",
                        this.buildPieChartDataProvider(this.currentData.min, this.currentData.max),
                        14,
                        325,
                        false,
                        function ( e )
                        {
                            $.each( e.chart.chartData, function ( i, item ) {
                                const index = getIndexOf(result[0].data, item.dataContext);
                                if (index >= 0)
                                    result[0].data[index].color = item.color
                            } );
                            const interval = setInterval( function ()
                                                        {
                                                            if( window.fabric )
                                                            {
                                                                clearTimeout( interval );
                                                                // CAPTURE CHART
                                                                e.chart['export'].capture( {}, function ()
                                                                {
                                                                    // SAVE TO JPG
                                                                    this.toJPG( {}, function ( base64 )
                                                                    {
                                                                        result[0].image = base64;
                                                                        if( callback )
                                                                            callback( result );
                                                                    } );
                                                                } )
                                                            }
                                                        }, 50 );
                        } );
};

ChartController.prototype._exportBarChart = function( barCount, horizontal, breaker, callback ) {
    const self = this;
    const result = [];

    const minimumValue = this.chart.valueAxes[0].min;
    const maximumValue = this.chart.valueAxes[0].max;

    const dataParts = [];
    for (let i = 0; i < Math.ceil(this.currentData.chartPoints.length / barCount); i++){
        dataParts.push(this.currentData.chartPoints.slice(i * barCount, i * barCount + barCount));
    }
    if (dataParts[dataParts.length - 1].length < barCount){
        const emptyItem = {
            category: {}, // ant need return string value
            value: ""
        };
        for(let i = dataParts[dataParts.length - 1].length; i < barCount; i++)
            dataParts[dataParts.length - 1].push(emptyItem);
    }

    let chart;

    const setPart = (i) => {
        if (i === dataParts.length){
            callback( result );
            return;
        }

        result[i] = {};
        result[i].data = dataParts[i];

        if (i === 0) {

            chart = this.buildBarChart("chartPart",
                this.getBarChartConfiguration(
                    dataParts[i],
                    this.currentData.title,
                    {
                        horizontal: this.currentState.chartType === ChartType.HORIZONTAL_BAR,
                        withoutAnimation: true,
                        withoutScroll: true,
                        minimum: minimumValue,
                        maximum: maximumValue,
                        fontSize: 14
                    }),
                function(e) {
                    const interval = setInterval(function() {
                        if (window.fabric) {
                            clearTimeout(interval)
                            // CAPTURE CHART
                            e.chart['export'].capture({}, function() {
                                // SAVE TO JPG
                                this.toJPG({}, function(base64) {
                                    if (!breaker['break']) {
                                        result[i].image = base64
                                        setPart(++i)
                                    }
                                })
                            })
                        }
                    }, 50)
                })
        }
        else
        {
            chart.dataProvider = this._getDataProvider(dataParts[i], this.currentState.groupingLevel, this.currentState.stackColumns)
            chart.validateData()
            chart.validateNow()
        }
    }

    setPart( 0 );
};
ChartController.prototype.getCurrentBarCount = function() {
    return this.currentBarCount ? this.currentBarCount : 0;
};

ChartController.prototype.breakExport = function( ) {
    if (this.currentExportBreaker)
        this.currentExportBreaker['break'] = true;
};

ChartController.prototype._extendCurrentStateWithFormattedFunctions = function( groupingLevel, reportChartType )
{
    this.currentState.formatCategoryFunction = this.chartFormatter.getFormatCategoryFunctionForChart( groupingLevel, false, 17, false );
    this.currentState.formatHBarChartCursorLabelFunction = this.chartFormatter.getFormatCategoryFunctionForChart( groupingLevel, true, 18, true );
    this.currentState.formatVBarChartCursorLabelFunction = this.chartFormatter.getFormatCategoryFunctionForChart( groupingLevel, true, 30, true );
    this.currentState.formatValueFunction    = this.chartFormatter.getValueFormattedFunction( reportChartType );
    this.currentState.formatLabelFunction    = this.chartFormatter.getFormatPieLabelFunction( groupingLevel );
    this.currentState.toStringFunction       = this.chartFormatter.getCategoryToStringFunction( groupingLevel );
};

ChartController.prototype._buildGraphs = function(settings) {
    const total = this.currentData.total
    const chartColorSchemeById = ReportChartUtils.getChartColorSchemeById(this.currentState.colorId)
    const userColor = chartColorSchemeById.user
    const userTeamColor = chartColorSchemeById.userTeam
    const notAssignedColor = chartColorSchemeById.notAssigned

    if (this.currentState.stackColumns) {
        return [
            {
                "fillAlphas": 1,
                "fillColors": userColor,
                "labelPosition": "middle",
                "labelRotation": settings.horizontal ? 0 : 270,
                "balloonFunction": () => {},
                "lineThickness": 0,
                "type": "column",
                "valueField": "me",
                "balloon": {
                    "maxWidth": 320,
                },
            },
            {
                "fillAlphas": 1,
                "fillColors": userTeamColor,
                "labelPosition": "middle",
                "labelRotation": settings.horizontal ? 0 : 270,
                "balloonFunction": () => {},
                "lineThickness": 0,
                "type": "column",
                "valueField": "myTeam",
                "balloon": {
                    "maxWidth": 320,
                },
            },
            {
                "fillAlphas": 1,
                "fillColors": notAssignedColor,
                "labelPosition": "middle",
                "labelRotation": settings.horizontal ? 0 : 270,
                "balloonFunction": (item) => {
                    const dataContext = item.dataContext
                    const showPercents = this.currentState.showPercents

                    let getTextFor = (value, total) => {
                        return this.currentState.formatValueFunction(value) + (showPercents ? " (" + (this.numberFormatter.format(value * 100 / total)) + "%)" : "")
                    }

                    if (this.currentState.stackColumns) {
                        let text = '<div class = "chartBalloon"><b>' + this.currentState.toStringFunction(dataContext.category) + '</b>'
                        const reportTitle = this.currentData.title
                        const balloonTotalTitle = reportTitle.substr(0, reportTitle.lastIndexOf(' ('))
                        text += '<br><b>' + balloonTotalTitle + ': ' + getTextFor((dataContext.total), total) + '</b>'
                        if (dataContext.notAssignedUsers) {
                            text += '<br>'
                                    + this._getColorMarkerForBalloon(notAssignedColor) + ' '
                                    + messageResource.getMessage('report.create_chart.stacked_columns_popup.not_assigned_users') + ': '
                                    + getTextFor(dataContext.notAssignedUsers, total)
                        }
                        if (dataContext.myTeam) {
                            text += '<br>'
                                    + this._getColorMarkerForBalloon(userTeamColor) + ' '
                                    + messageResource.getMessage('report.create_chart.stacked_columns_popup.user_team') + ': '
                                    + getTextFor(dataContext.myTeam, total)
                        }
                        if (dataContext.me) {
                            text += '<br>'
                                    + this._getColorMarkerForBalloon(userColor) + ' '
                                    + messageResource.getMessage("report.create_chart.stacked_columns_popup.user") + ': '
                                    + getTextFor(dataContext.me, total)
                        }
                        text += '</div>'
                        return text
                    } else {
                        return getTextFor(dataContext.value)
                    }
                },
                "lineThickness": 0,
                "type": "column",
                "valueField": "notAssignedUsers",
                "balloon": {
                    "maxWidth": 320,
                },
            },
        ]
    } else {
        return [
            {
                "fillAlphas": 1,
                "fillColorsField": "color",
                "labelPosition": "middle",
                "labelRotation": settings.horizontal ? 0 : 270,
                "labelText": "[[value]]",
                "labelFunction": (item) => {
                    return this.currentState.formatValueFunction(item.dataContext.value) +
                           (this.currentState.showPercents && item.dataContext.value.toString().length ?
                           " " + "(" + this.numberFormatter.format(total === 0 ? 0.00 : (item.dataContext.value * 100 / total)) + "%)" : "")
                },
                "balloonFunction": (item) => {
                    return this.currentState.formatValueFunction(item.dataContext.value)
                },
                "lineThickness": 0,
                "type": "column",
                "valueField": "value"
            }
        ]
    }
}

ChartController.prototype._getColorMarkerForBalloon = function(color) {
    return '<span style="background-color:' + color + ';">&nbsp&nbsp</span>'
}

ChartController.prototype._buildStackedDataProvider = function(chartPoints) {
    return chartPoints.map((point) => {
        const value = point.value
        return {
            category: point.category,
            me: value.userPoint,
            myTeam: value.userTeamPoint,
            notAssignedUsers: value.notAssignedUsersPoint,
            total: value.total,
        }
    })
}

ChartController.prototype._buildSimpleColumnDataProvider = function(chartPoints, groupingLevel) {
    const colorScheme = ReportChartUtils.getChartColorSchemeById(this.currentState.colorId)
    return chartPoints.map((point) => {
        const value = point.value
        const othersColumn = this._isOtherUsersData(groupingLevel, point)
        return {
            category: point.category,
            value: value.total,
            color: othersColumn ?
                   colorScheme.notAssigned :
                   colorScheme.singleColumn,
        }
    })
}

ChartController.prototype._isOtherUsersData = function(groupingLevel, chartPoint) {
    return (ReportGroupingLevel.USER_GROUP === groupingLevel && chartPoint.category.id === OTHER_USER_GROUPS_ENTITY_ID) ||
           (ReportGroupingLevel.TIME_ZONE_GROUP === groupingLevel && chartPoint.category.id === OTHER_USER_GROUPS_ENTITY_ID) ||
           (ReportGroupingLevel.USER === groupingLevel && chartPoint.category.id === OTHER_USERS_ENTITY_ID)
}

ChartController.prototype._getDataProvider = function(chartPoints, groupingLevel, stackColumns) {
    return stackColumns ? this._buildStackedDataProvider(chartPoints) : this._buildSimpleColumnDataProvider(chartPoints, groupingLevel)
}



// WEBPACK FOOTER //
// ./web/js/reports/charts/ChartController.js